import * as React from 'react';
import b_ from 'b_';
import cn from 'classnames';
import { Field, WrappedFieldProps, BaseFieldProps } from 'redux-form';
import { currencyToSymbol } from '../../components/MoneyView';
import { InputView, Props as InputProps } from '../InputView';
import { Stages } from './Stages';
import { Bar } from './Bar';

import './index.scss';

const b = b_.with('amount-input');

interface Props extends InputProps {
  currency?: string;
  max?: number;
  min?: number;
  stagesCount?: number;
}

export const AmountInputView: React.FC<Props & WrappedFieldProps> = ({
  max,
  currency,
  className,
  classNameLabel,
  input,
  stagesCount,
  label,
  inside,
  ...props
}) => (
  <InputView
    label={currency ? currencyToSymbol(currency) : label}
    className={cn(b(), className)}
    classNameLabel={cn(b('label'), classNameLabel)}
    input={input}
    inside={
      <>
        {max && <Bar className={b('bar')} max={max} value={input.value} />}
        {inside}
      </>
    }
    {...props}
  >
    {max && currency && (
      <Stages
        className={b('stages')}
        count={stagesCount || 4}
        max={max}
        currency={currency}
        onClick={input.onChange}
      />
    )}
  </InputView>
);

export const cleanInput = (value: string | number, allowNegative = true) => {
  let result = `${value}`.replace(/[^0-9-.]+/g, '');

  // keep first minus only
  const isNegative = result[0] === '-';
  result = result.replace(/-/g, '');
  if (isNegative && allowNegative) {
    result = `-${result}`;
  }

  // keep first dot only
  if (result.indexOf('.') !== -1) {
    const [integer, ...rest] = result.split('.');
    result = `${integer}.${rest.join('')}`;
  }

  return result;
};

export const AmountInput: React.FC<Props & BaseFieldProps> = (props) => {
  function normalize(value: string) {
    const { max } = props;
    const precision = 2;
    let cleaned = cleanInput(value, false);

    const result = +(cleaned.match(/[-.]$/) ? `${cleaned}0` : cleaned); // avoid NaN for `-` and `1.` inputs
    if (typeof max === 'number' && result > max) {
      cleaned = `${max}`;
    }

    const [, decimals] = cleaned.split('.');

    if (decimals && decimals.length > precision) {
      cleaned = cleaned.substr(0, cleaned.length - (decimals.length - precision));
    }

    return cleaned;
  }

  return <Field component={AmountInputView} normalize={normalize} {...props} />;
};
